import React, { useState } from 'react';
import useScrollAnimation from './useScrollAnimation';

const specsData = {
  'i18-pro': {
    name: 'Peace i18 PRO (5G Flagship)',
    tagline: 'Beyond Power. Beyond Limits.',
    poster: '/Photo/peace-i17-pro-poster.jpg',
    categories: [
      {
        id: 'display',
        title: 'Display & Design',
        items: [
          { label: 'Screen Size', val: '6.61" FHD+ Immersive Vision' },
          { label: 'Refresh Rate', val: '120Hz Ultra-Smooth Refresh Rate' },
          { label: 'Resolution', val: 'FHD+ High Definition (Edge-to-Edge)' },
          { label: 'Design Badges', val: 'Powerful Performance, Advanced Security, AI Intelligent Experience, Premium Design' },
          { label: 'Finish / Colors', val: 'Solar Orange, Titanium Silver, Obsidian Black' }
        ]
      },
      {
        id: 'camera',
        title: 'Cameras & Imaging',
        items: [
          { label: 'Rear Camera', val: '20MP Rear Camera (Ultra Clarity AI Powered)' },
          { label: 'Front Camera', val: '8MP Front Camera (Perfect Selfies Every Time)' },
          { label: 'AI Features', val: 'AI Intelligent Experience, Auto Scene Detection, HDR Portrait Mode' },
          { label: 'Video Capture', val: 'High-Definition 1080p Video Recording with Audio Zoom' }
        ]
      },
      {
        id: 'power',
        title: 'Battery & Charging',
        items: [
          { label: 'Battery Capacity', val: '5000 mAh High-Density Cell' },
          { label: 'Battery Life', val: 'All-Day Power on Continuous Heavy Usage' },
          { label: 'Charging', val: 'Fast Charging Supported via USB-C' }
        ]
      },
      {
        id: 'connectivity',
        title: 'Storage & Connectivity',
        items: [
          { label: 'Storage', val: '128GB Internal Storage (More Space, More Freedom)' },
          { label: 'Network', val: '5G Ultra-Fast Connectivity' },
          { label: 'SIM Support', val: 'DUAL SIM (Stay Connected Anywhere)' },
          { label: 'Warranty & Stock', val: '1 Year Official Warranty (Peace Certified) | Limited Stock' }
        ]
      }
    ]
  },
  'nova-17-pro': {
    name: 'Peace NOVA 18 PRO',
    tagline: 'The Ultimate Smartphone Experience | Powered for Everyday Excellence',
    poster: '/Photo/peace-nova-17-pro-poster.jpg',
    categories: [
      {
        id: 'display',
        title: 'Display & Design',
        items: [
          { label: 'Screen Size', val: '6.56" HD+ Display' },
          { label: 'Display Type', val: 'Vibrant HD+ Edge-to-Edge Color Screen' },
          { label: 'Color Options', val: 'Silver, Orange, Dark Gray' },
          { label: 'Chassis', val: 'Ergonomic Slim Profile with Polished Accents' }
        ]
      },
      {
        id: 'camera',
        title: 'Cameras & Imaging',
        items: [
          { label: 'Rear Camera', val: '50MP Rear Camera (Master Ultra Clarity)' },
          { label: 'Front Camera', val: '8MP Front Camera' },
          { label: 'Camera Modes', val: 'AI Scene Optimizer, Night Shot, Beauty Portrait' },
          { label: 'Video Capture', val: 'HD Video Recording with Crisp Audio' }
        ]
      },
      {
        id: 'power',
        title: 'Battery & Charging',
        items: [
          { label: 'Battery Capacity', val: '5000 mAh Inbuilt Battery' },
          { label: 'Battery Life', val: 'Full Day Power for Gaming, Streaming, and Multitasking' },
          { label: 'Charging', val: 'Standard USB-C Quick Charge' }
        ]
      },
      {
        id: 'connectivity',
        title: 'Storage & Connectivity',
        items: [
          { label: 'RAM + ROM', val: '4GB RAM + 64GB ROM' },
          { label: 'Expandable Storage', val: 'Expandable Storage up to 128GB via MicroSD' },
          { label: 'Network', val: '4G Connectivity' },
          { label: 'Warranty', val: '1 Year Warranty' }
        ]
      }
    ]
  },
  'sc26': {
    name: 'Peace SC26 (Aura26)',
    tagline: 'Performance Meets Peace. | Re-engineered. Revolutionary.',
    poster: '/Photo/peace-sc26-poster.jpg',
    categories: [
      {
        id: 'display',
        title: 'Display & Design',
        items: [
          { label: 'Screen Size', val: '6.56" HD+ Display' },
          { label: 'Stylus Pen', val: 'Built-in Precision Stylus Pen included in device chassis' },
          { label: 'Design Badges', val: 'Sleek Design, Built for the future, Capture Every Moment, Reliable & Durable, Comfortable Grip' },
          { label: 'Color Finishes', val: 'Sunset Orange (with Matching Stylus), Glacier Silver, Stealth Black' }
        ]
      },
      {
        id: 'camera',
        title: 'Cameras & Imaging',
        items: [
          { label: 'Rear Camera', val: '20MP Rear Camera (High Definition Clarity)' },
          { label: 'Front Camera', val: '8MP Front Camera' },
          { label: 'Photo Features', val: 'Portrait Bokeh, AI HDR, Panorama, Instant Shutter' },
          { label: 'Video Capture', val: 'HD Video Recording with Smooth Stabilization' }
        ]
      },
      {
        id: 'power',
        title: 'Battery & Charging',
        items: [
          { label: 'Battery Capacity', val: '5000 mAh Long-lasting Battery' },
          { label: 'Battery Performance', val: 'Engineered for multi-day standby and prolonged screen-on time' },
          { label: 'Charging', val: 'USB-C Fast Charging Interface' }
        ]
      },
      {
        id: 'connectivity',
        title: 'Storage & Connectivity',
        items: [
          { label: 'Memory & Storage Options', val: 'Available in 4GB+128GB or 6GB RAM+64GB ROM configurations' },
          { label: 'SIM Type', val: 'Dual SIM Standby' },
          { label: 'Warranty', val: '1 Year Warranty (Peace of Mind, Every Day)' },
          { label: 'Call to Action', val: 'Choose Peace. Choose SC26.' }
        ]
      }
    ]
  }
};

const SpecificationsSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.15 });
  const [selectedModelKey, setSelectedModelKey] = useState('i18-pro');
  const [activeCategoryTab, setActiveCategoryTab] = useState('display');

  const currentModelData = specsData[selectedModelKey];
  const currentCategory = currentModelData.categories.find((c) => c.id === activeCategoryTab) || currentModelData.categories[0];

  return (
    <section 
      ref={sectionRef} 
      className={`specs-section ${isVisible ? 'is-visible' : ''}`}
      id="specs"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center specs-header scroll-fade-up">
          <span className="section-eyebrow">Technical Specifications</span>
          <h2 className="section-headline">Detailed Specs for Every Model.</h2>
          <p className="section-subheadline">
            Select a device below to inspect the comprehensive hardware specifications.
          </p>
        </div>

        {/* Model Tabs Selector */}
        <div className="specs-model-selector-row scroll-fade-up">
          {Object.keys(specsData).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedModelKey(key)}
              className={`specs-model-pill-btn ${selectedModelKey === key ? 'active' : ''}`}
            >
              {specsData[key].name}
            </button>
          ))}
        </div>

        {/* Specs Category Sub-Tabs */}
        <div className="specs-tabs-row scroll-fade-up">
          {currentModelData.categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategoryTab(cat.id)}
              className={`specs-tab-btn ${activeCategoryTab === cat.id ? 'active' : ''}`}
            >
              {cat.title}
            </button>
          ))}
        </div>

        {/* Specs Detail Box */}
        <div className="specs-content-card scroll-scale-up" key={`${selectedModelKey}-${activeCategoryTab}`}>
          <div className="specs-card-header">
            <div>
              <h3 className="specs-category-title">{currentCategory.title}</h3>
              <p className="specs-category-sub">{currentModelData.name} — {currentModelData.tagline}</p>
            </div>
            <span className="specs-model-tag">1 Year Warranty</span>
          </div>

          <div className="specs-grid-table">
            {currentCategory.items.map((item, idx) => (
              <div key={idx} className="spec-row-item">
                <div className="spec-item-label">{item.label}</div>
                <div className="spec-item-value">{item.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecificationsSection;
